import { h } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { promiseWrapper } from 'utils/promise'
import CalculatorInput from './input-element'
import MainChartView from './main-chart'
import CalculatorDirectionSelect from './direction-select'
import CalculatorCurrencySelect from './currency-select'
import { CalculatorDirection, CurrencyValueForm, CalculatorSavings } from 'models/apps'
import { STATIC_CALCULATOR_SAVINGS_DATA, STATIC_EXCHANGE_RATES } from '../config'
import { BankData, MarketEstimate } from 'models/market'
import { validateCurrencyValue } from 'app-utils/validate-storage-value'

declare const USER_BASE_URL_WT: string
const MARKET_ESTIMATE_API = '/api/public/marketEstimate'
const DEFAULT_CURRENCY_VALUE = '1000'

const MainCalculatorView = () => {
  const [isErrorVisible, setErrorVisible] = useState<boolean>(false)
  const [currentOffer, setCurrentOffer] = useState<BankData[]>(STATIC_EXCHANGE_RATES)
  const [savings, setSavings] = useState<CalculatorSavings>(STATIC_CALCULATOR_SAVINGS_DATA)
  const [direction, setDirection] = useState<CalculatorDirection>('BUY')
  const [buyCurrency, setBuyCurrency] = useState<string>('EUR')
  const [sellCurrency, setSellCurrency] = useState<string>('PLN')
  const [value, setValue] = useState<CurrencyValueForm>({
    currentValue: DEFAULT_CURRENCY_VALUE,
    isValueCorrect: true,
  })

  useEffect(() => {
    setValue({ currentValue: validateCurrencyValue(), isValueCorrect: true })

    const actualCurrency = document
      .querySelector<HTMLParagraphElement>('.hero__table-information')
      .textContent.slice(-3)
    setBuyCurrency(actualCurrency)
  }, [])

  useEffect(() => {
    if (!value.isValueCorrect) {
      return
    }

    setErrorVisible(false)
    const fetchData = async (): Promise<void> => {
      try {
        const response = await promiseWrapper<MarketEstimate, undefined>(buildLink(), 'GET')
        setCurrentOffer(response['exchange_offers'])
        setSavings(
          response['one_time_saving']
            ? { one_time_saving: response['one_time_saving'], yearly_savings: response['yearly_savings'] }
            : null,
        )
      } catch (e) {
        setErrorVisible(true)
      }
    }
    fetchData()

    const interval = setInterval(fetchData, 10000)

    return () => clearInterval(interval)
  }, [direction, buyCurrency, sellCurrency, value])

  const buildLink = (): string => {
    return `${USER_BASE_URL_WT}${MARKET_ESTIMATE_API}/${direction}/${value.currentValue}/${buyCurrency}/${sellCurrency}`
  }

  const removeStaticElements = (): void => {
    const elementsToRemove =
      typeof window !== 'undefined' ? Array.from(document.querySelectorAll<HTMLElement>('[data-remove-me]')) : []
    elementsToRemove.forEach((element) => element.remove())
  }

  return (
    <div className="hero-form">
      <div className="hero-form__main-section">
        <p className="hero-form__title">Oblicz, ile możesz zaoszczędzić!</p>
        <div className="hero-form__holder">
          <div className="hero-form__flex-items">
            <div>Chcę</div>
            <CalculatorDirectionSelect
              direction={direction}
              handleSetDirection={setDirection}
            />
          </div>
          <CalculatorInput
            value={value}
            setValue={setValue}
          />
          <div className="hero-form__flex-items">
            <CalculatorCurrencySelect
              isBuyComponent={true}
              buyCurrency={buyCurrency}
              sellCurrency={sellCurrency}
              handleSetBuyCurrency={setBuyCurrency}
              handleSetSellCurrency={setSellCurrency}
            />
            <p> za </p>
            <CalculatorCurrencySelect
              isBuyComponent={false}
              buyCurrency={buyCurrency}
              sellCurrency={sellCurrency}
              handleSetBuyCurrency={setBuyCurrency}
              handleSetSellCurrency={setSellCurrency}
            />
          </div>
        </div>
        {isErrorVisible && <p className="hero-form__error-info">Błąd komunikacji z serwerem</p>}
      </div>
      {currentOffer &&
        (removeStaticElements(),
        (
          <MainChartView
            incomingBanksData={currentOffer}
            savings={savings}
            sellCurrency={sellCurrency}
            direction={direction}
            value={value.currentValue}
            buyCurrency={buyCurrency}
          />
        ))}
    </div>
  )
}

export default MainCalculatorView
